import { ComboboxFormField } from '@components/Combobox'
import { memo, useMemo } from 'react'
import type { FieldValues, Path } from 'react-hook-form'
import { getCountries } from 'react-phone-number-input'
import nb from 'react-phone-number-input/locale/nb'

export const SCANDINAVIAN_COUNTRIES = ['DK', 'NO', 'SE', 'FI', 'IS']

interface CountriesFormFieldProps<F extends FieldValues> {
  name: Path<F>
  label?: string
  description?: string
  helpText?: string
  placeholder?: string
  searchPlaceholder?: string
  noResultsText?: string
  disabled?: boolean
  className?: string
  allowAllCountries?: boolean
}

const getCountryItems = (allowAllCountries?: boolean) => {
  return getCountries()
    .filter((country) => {
      if (!allowAllCountries) {
        return SCANDINAVIAN_COUNTRIES.includes(country)
      }
      return true
    })
    .map((country) => {
      const countryName = nb[country as keyof typeof nb] || country

      return {
        value: country,
        label: countryName,
      }
    })
}

export const CountriesFormField = memo(function CountriesFormField<F extends FieldValues>({
  name,
  label = 'Land',
  description,
  helpText,
  placeholder,
  searchPlaceholder,
  noResultsText,
  disabled,
  className,
  allowAllCountries = false, // Default to Scandinavian-only
}: CountriesFormFieldProps<F>) {
  const countryItems = useMemo(() => getCountryItems(allowAllCountries), [allowAllCountries])

  return (
    <ComboboxFormField
      name={name}
      label={label}
      description={description}
      helpText={helpText}
      items={countryItems}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      noResultsText={noResultsText}
      disabled={disabled}
      className={className}
    />
  )
})

CountriesFormField.displayName = 'CountriesFormField'
